// components/InstallButton.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Tooltip,
  Box,
  Typography,
  IconButton,
  Drawer,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Chip,
  Avatar,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  InstallMobile as InstallIcon,
  GetApp as GetAppIcon,
  Close as CloseIcon,
  IosShare as IosShareIcon,
  AddBoxOutlined as AddBoxIcon,
  MoreVert as MoreIcon,
  Android as AndroidIcon,
  PhoneIphone as AppleIcon,
  DesktopWindows as DesktopIcon,
  CheckCircle as CheckIcon,
  BoltOutlined as BoltIcon,
  WifiOff as OfflineIcon,
} from '@mui/icons-material';

/* ============================================================
   InstallButton — Modern PWA install prompt (2025)
   Handles:
   • Android Chrome / Samsung Internet (beforeinstallprompt)
   • Desktop Chrome / Edge (native prompt)
   • iOS Safari (manual instructions)
   • iOS Chrome / Firefox (manual via Safari)
   • Already-installed detection (all platforms)
   • Dismissed for N days persistence
   ============================================================ */

const DISMISS_KEY = 'pwa_install_dismissed_until';
const DISMISS_DAYS = 7;

const InstallButton = ({ language, variant = 'toolbar' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ar';

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSDrawer, setShowIOSDrawer] = useState(false);
  const [showDesktopDialog, setShowDesktopDialog] = useState(false);
  const [browserName, setBrowserName] = useState('');

  // ---------- Detect platform ----------
  const detectPlatform = useCallback(() => {
    const ua = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;

    // iOS detection (iPhone/iPad/iPod OR iPad on iOS 13+)
    const iOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (platform === 'MacIntel' && maxTouchPoints > 1);

    setIsIOS(iOS);

    // Standalone mode (already installed)
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Browser name for tailored instructions
    let name = 'unknown';
    if (/Edg/i.test(ua)) name = 'edge';
    else if (/OPR|Opera/i.test(ua)) name = 'opera';
    else if (/SamsungBrowser/i.test(ua)) name = 'samsung';
    else if (/Firefox|FxiOS/i.test(ua)) name = 'firefox';
    else if (/CriOS/i.test(ua)) name = 'chrome-ios';
    else if (/Chrome/i.test(ua)) name = 'chrome';
    else if (/Safari/i.test(ua)) name = 'safari';
    setBrowserName(name);

    return { iOS, standalone, name };
  }, []);

  // ---------- Dismiss check ----------
  const isDismissed = () => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return Date.now() < until;
    } catch {
      return false;
    }
  };

  const dismissForDays = (days = DISMISS_DAYS) => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + days * 24 * 60 * 60 * 1000)
      );
    } catch {}
    setShowButton(false);
  };

  // ---------- Effects ----------
  useEffect(() => {
    const { iOS, standalone, name } = detectPlatform();

    if (standalone) {
      setShowButton(false);
      return;
    }
    if (isDismissed()) {
      setShowButton(false);
      return;
    }

    // Android / Desktop Chrome / Edge / Samsung Internet
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowButton(true);
    };

    // Fired when app is installed
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShowButton(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // iOS: no beforeinstallprompt event → show manual button
    if (iOS) {
      const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
      // Show button on iOS Safari and other iOS browsers (they all install via Safari)
      setShowButton(true);
    }

    // Android but beforeinstallprompt hasn't fired yet (Chrome waits for engagement)
    if (!iOS && name === 'chrome' && isMobile) {
      // Fallback: show button after 3s if user has visited at least 2 times
      const visits = parseInt(localStorage.getItem('pwa_visits') || '0', 10) + 1;
      localStorage.setItem('pwa_visits', String(visits));
      if (visits >= 2) {
        const timer = setTimeout(() => {
          if (!deferredPrompt) setShowButton(true);
        }, 3000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
          window.removeEventListener('appinstalled', handleAppInstalled);
        };
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [detectPlatform, isMobile]);

  // ---------- Handlers ----------
  const handleInstall = async () => {
    // Native prompt available (Android Chrome/Edge/Samsung, Desktop Chrome/Edge)
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowButton(false);
          setDeferredPrompt(null);
        } else {
          dismissForDays(3); // user said "not now" → wait 3 days
        }
      } catch (err) {
        console.warn('Install prompt failed:', err);
        setDeferredPrompt(null);
        // Fallback to manual instructions
        if (isIOS) setShowIOSDrawer(true);
        else setShowDesktopDialog(true);
      }
      return;
    }

    // iOS → manual instructions
    if (isIOS) {
      setShowIOSDrawer(true);
      return;
    }

    // Desktop without native prompt → manual instructions
    setShowDesktopDialog(true);
  };

  const handleIOSClose = () => {
    setShowIOSDrawer(false);
    dismissForDays(DISMISS_DAYS);
  };

  const handleDesktopClose = () => {
    setShowDesktopDialog(false);
    dismissForDays(DISMISS_DAYS);
  };

  if (!showButton || isStandalone) return null;

  // ---------- Styles ----------
  const goldGradient = 'linear-gradient(135deg, #E6B800 0%, #FFD700 100%)';

  const btnSx =
    variant === 'toolbar'
      ? {
          minWidth: 'auto',
          height: 40,
          px: { xs: 1.2, sm: 1.6 },
          borderRadius: '999px',
          background: goldGradient,
          color: '#111',
          fontWeight: 700,
          fontSize: { xs: '0.72rem', sm: '0.78rem' },
          textTransform: 'none',
          boxShadow: '0 6px 18px rgba(230, 184, 0, 0.28)',
          transition: 'all 0.25s ease',
          animation: 'installPulse 2.4s ease-in-out infinite',
          '&:hover': {
            background: 'linear-gradient(135deg, #FFD700 0%, #E6B800 100%)',
            boxShadow: '0 10px 26px rgba(230, 184, 0, 0.45)',
            transform: 'translateY(-1px)',
          },
          '& .MuiButton-startIcon': {
            mr: { xs: 0, sm: 0.6 },
            ml: { xs: 0, sm: 0 },
          },
        }
      : {
          background: goldGradient,
          color: '#111',
          fontWeight: 800,
          px: 2.5,
          py: 1.2,
          borderRadius: '999px',
          textTransform: 'none',
          fontSize: '0.9rem',
          boxShadow: '0 10px 26px rgba(230, 184, 0, 0.3)',
          transition: 'all 0.25s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, #FFD700 0%, #E6B800 100%)',
            boxShadow: '0 14px 34px rgba(230, 184, 0, 0.5)',
            transform: 'translateY(-2px)',
          },
        };

  return (
    <>
      {/* Global keyframes injected once */}
      <style>{`
        @keyframes installPulse {
          0%, 100% { box-shadow: 0 6px 18px rgba(230,184,0,0.28); }
          50% { box-shadow: 0 6px 26px rgba(230,184,0,0.55); }
        }
      `}</style>

      <Tooltip
        title={isRtl ? 'ثبّت التطبيق على جهازك' : 'Install app on your device'}
        arrow
        enterTouchDelay={0}
      >
        <Button
          variant="contained"
          onClick={handleInstall}
          startIcon={isMobile ? <InstallIcon /> : <GetAppIcon />}
          aria-label={isRtl ? 'تثبيت التطبيق' : 'Install App'}
          sx={btnSx}
        >
          {!isMobile && (isRtl ? 'تثبيت' : 'Install')}
        </Button>
      </Tooltip>

      {/* ============ iOS Instructions Drawer (bottom sheet) ============ */}
      <Drawer
        anchor="bottom"
        open={showIOSDrawer}
        onClose={handleIOSClose}
        PaperProps={{
          sx: {
            background: 'rgba(20, 20, 20, 0.96)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            borderTop: '1px solid rgba(230, 184, 0, 0.25)',
            color: '#fff',
            maxWidth: 560,
            mx: 'auto',
            width: '100%',
            pb: 3,
          },
        }}
      >
        <Box sx={{ p: 3, pt: 2, dir: isRtl ? 'rtl' : 'ltr' }}>
          {/* Grabber */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              mx: 'auto',
              mb: 2,
            }}
          />

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(230,184,0,0.12)',
                color: '#FFD700',
                mr: isRtl ? 0 : 1.5,
                ml: isRtl ? 1.5 : 0,
              }}
            >
              <AppleIcon />
            </Avatar>
            <Box sx={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                {isRtl ? 'ثبّت التطبيق على iPhone' : 'Install on iPhone / iPad'}
              </Typography>
              <Typography sx={{ color: '#9A9A9A', fontSize: '0.75rem' }}>
                {isRtl ? 'اتبع الخطوات في متصفح Safari' : 'Follow these steps in Safari'}
              </Typography>
            </Box>
            <IconButton onClick={handleIOSClose} sx={{ color: '#9A9A9A' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Steps */}
          <Stack spacing={1.5} sx={{ mb: 2.5 }}>
            {[
              {
                icon: <IosShareIcon />,
                title: isRtl ? 'افتح في Safari' : 'Open in Safari',
                desc: isRtl
                  ? 'إذا كنت تستخدم Chrome أو Firefox على iOS، انسخ الرابط والصقه في Safari'
                  : 'If you\'re on Chrome/Firefox on iOS, copy the link and open it in Safari',
              },
              {
                icon: <IosShareIcon />,
                title: isRtl ? 'اضغط زر المشاركة' : 'Tap the Share button',
                desc: isRtl
                  ? 'الزر المربع مع سهم للأعلى أسفل الشاشة'
                  : 'The square icon with an up-arrow at the bottom',
              },
              {
                icon: <AddBoxIcon />,
                title: isRtl ? 'اختر "إضافة إلى الشاشة الرئيسية"' : 'Choose "Add to Home Screen"',
                desc: isRtl
                  ? 'اسحب للأسفل في قائمة المشاركة حتى تجده'
                  : 'Scroll down in the share sheet to find it',
              },
              {
                icon: <CheckIcon />,
                title: isRtl ? 'اضغط "إضافة"' : 'Tap "Add"',
                desc: isRtl
                  ? 'سيظهر التطبيق على شاشتك الرئيسية'
                  : 'The app icon will appear on your home screen',
              },
            ].map((step, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  flexDirection: isRtl ? 'row-reverse' : 'row',
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'rgba(230,184,0,0.12)',
                    color: '#FFD700',
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                  }}
                >
                  {step.icon}
                </Avatar>
                <Box sx={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: '#9A9A9A', fontSize: '0.75rem', mt: 0.25, lineHeight: 1.4 }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          <Button
            fullWidth
            onClick={handleIOSClose}
            sx={{
              background: goldGradient,
              color: '#111',
              fontWeight: 800,
              py: 1.4,
              borderRadius: '999px',
              textTransform: 'none',
              boxShadow: '0 8px 20px rgba(230,184,0,0.3)',
              '&:hover': { transform: 'translateY(-1px)' },
            }}
          >
            {isRtl ? 'فهمت' : 'Got it'}
          </Button>
        </Box>
      </Drawer>

      {/* ============ Desktop / Fallback Instructions Dialog ============ */}
      <Dialog
        open={showDesktopDialog}
        onClose={handleDesktopClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        PaperProps={{
          sx: {
            background: 'rgba(20, 20, 20, 0.96)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(230, 184, 0, 0.25)',
            borderRadius: '22px',
            color: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'rgba(230,184,0,0.12)', color: '#FFD700' }}>
              <DesktopIcon />
            </Avatar>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
              {isRtl ? 'تثبيت التطبيق' : 'Install App'}
            </Typography>
          </Box>
          <IconButton onClick={handleDesktopClose} sx={{ color: '#9A9A9A' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, dir: isRtl ? 'rtl' : 'ltr' }}>
          {/* Feature highlights */}
          <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<BoltIcon sx={{ fontSize: '1rem !important', color: '#FFD700 !important' }} />}
              label={isRtl ? 'أسرع' : 'Faster'}
              size="small"
              sx={{ bgcolor: 'rgba(230,184,0,0.1)', color: '#FFD700', fontWeight: 600 }}
            />
            <Chip
              icon={<OfflineIcon sx={{ fontSize: '1rem !important', color: '#FFD700 !important' }} />}
              label={isRtl ? 'يعمل بدون إنترنت' : 'Works offline'}
              size="small"
              sx={{ bgcolor: 'rgba(230,184,0,0.1)', color: '#FFD700', fontWeight: 600 }}
            />
            <Chip
              icon={<CheckIcon sx={{ fontSize: '1rem !important', color: '#FFD700 !important' }} />}
              label={isRtl ? 'بدون متجر' : 'No store'}
              size="small"
              sx={{ bgcolor: 'rgba(230,184,0,0.1)', color: '#FFD700', fontWeight: 600 }}
            />
          </Stack>

          {/* Browser-specific instructions */}
          <Stack spacing={1.25}>
            {(browserName === 'edge' || browserName === 'chrome' || browserName === 'samsung' || browserName === 'opera') && (
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: '14px',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
                  {isRtl ? 'الطريقة الأسهل' : 'Easiest way'}
                </Typography>
                <Typography sx={{ color: '#B8B8B8', fontSize: '0.8rem', lineHeight: 1.6 }}>
                  {isRtl
                    ? 'ابحث عن أيقونة التثبيت (⊕ أو شاشة مع سهم) في شريط العنوان ثم اضغط "تثبيت".'
                    : 'Look for the install icon (⊕ or a screen with arrow) in the address bar, then click "Install".'}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                p: 1.75,
                borderRadius: '14px',
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
                {isRtl ? 'أو من القائمة' : 'Or from the menu'}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1} sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <MoreIcon sx={{ color: '#FFD700' }} />
                <Typography sx={{ color: '#B8B8B8', fontSize: '0.8rem' }}>
                  {isRtl
                    ? 'اضغط قائمة المتصفح (⋮) → "تثبيت التطبيق"'
                    : 'Open browser menu (⋮) → "Install app"'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Button
            fullWidth
            onClick={handleDesktopClose}
            sx={{
              mt: 3,
              background: goldGradient,
              color: '#111',
              fontWeight: 800,
              py: 1.3,
              borderRadius: '999px',
              textTransform: 'none',
              boxShadow: '0 8px 20px rgba(230,184,0,0.3)',
              '&:hover': { transform: 'translateY(-1px)' },
            }}
          >
            {isRtl ? 'حسناً' : 'Got it'}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallButton;